/* Cinematic Arena — R3F presentation layer for Battle Engine 2.0.
 *
 * Design contract (per iter30 AAA spec):
 *  - This component OWNS the visualisation of the pre-computed battle.
 *  - The hidden engine's event queue is consumed exactly as before; only
 *    the render surface is new. Winner is untouched.
 *  - HUD (tap zone, ultimate, primed consumables, timer, settings) is
 *    overlaid on top of the WebGL canvas as pointer-events HTML.
 *  - Reduced-motion / low-power devices fall back to the 2D LiveArena
 *    presentation so the game remains playable everywhere.
 */
import { Suspense, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { AdaptiveDpr, AdaptiveEvents, Preload } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette, ChromaticAberration } from '@react-three/postprocessing';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, ArrowLeft, Settings2, Zap, Sparkles, FlaskConical, Award, Flame } from 'lucide-react';
import api from '../lib/api';

import CinematicScene from '../components/arena3d/CinematicScene';
import CinematicCamera from '../components/arena3d/CinematicCamera';
import BattleInstructions from '../components/arena3d/BattleInstructions';
import { computeCombatPositions } from '../lib/species_registry';
import LiveArena from './LiveArena';
import { effectKindMeta } from '../components/arena3d/effect_meta';
import { buildEventQueue, INITIAL, reducer, ULTIMATE_TAP_THRESHOLD } from '../components/arena3d/battle_reducer';
import { getRoundSeconds } from '../lib/roundLength';

function loadSettings() {
  try {
    const s = JSON.parse(localStorage.getItem('wq_arena3d_settings')) || {};
    return {
      reducedMotion: !!s.reducedMotion || (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches),
      lowPower: !!s.lowPower,
      postFx: s.postFx !== false,
    };
  } catch { return { reducedMotion: false, lowPower: false, postFx: true }; }
}

export default function CinematicArena() {
  const nav = useNavigate();
  const loc = useLocation();
  const { battleId } = useParams();
  const [battle, setBattle] = useState(loc.state?.battle || null);
  const [loading, setLoading] = useState(!battle);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [roundSeconds] = useState(() => getRoundSeconds());
  const [timeLeft, setTimeLeft] = useState(roundSeconds);
  const [consumedItem, setConsumedItem] = useState(null);
  const [state, dispatch] = useReducer(reducer, null, () => INITIAL({ rounds: [] }));
  const consumeClearRef = useRef(null);

  // Load battle
  useEffect(() => {
    if (battle) return;
    if (!battleId) { setError('No battle provided'); setLoading(false); return; }
    let dead = false;
    api.get(`/battles/${battleId}`)
      .then(r => { if (!dead) { setBattle(r.data); setLoading(false); } })
      .catch(() => { if (!dead) { setError('Battle not found'); setLoading(false); } });
    return () => { dead = true; };
  }, [battle, battleId]);

  useEffect(() => { if (battle) dispatch({ type: 'INIT', battle }); }, [battle]);

  // Primed consumable cycle at battle start (shows tooltip + aura)
  useEffect(() => {
    if (!battle) return;
    const items = loc.state?.consumables || [];
    if (!items.length) return;
    let idx = 0;
    const show = () => {
      const it = items[idx];
      if (!it) return;
      const meta = effectKindMeta(it);
      setConsumedItem({ name: it.name || 'Consumable', color: meta.color, kind: meta.kind, description: `Primed · ${meta.description}` });
      clearTimeout(consumeClearRef.current);
      consumeClearRef.current = setTimeout(() => {
        setConsumedItem(null);
        idx += 1;
        if (idx < items.length) setTimeout(show, 250);
      }, 1800);
    };
    show();
    return () => clearTimeout(consumeClearRef.current);
  }, [battle, loc.state?.consumables]);

  // Timer
  useEffect(() => {
    if (!battle || state.finished) return;
    const iv = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(iv);
  }, [battle, state.finished]);
  useEffect(() => { if (timeLeft === 0 && !state.finished) dispatch({ type: 'FAST_FORWARD' }); }, [timeLeft, state.finished]);

  // Flash cleanup
  useEffect(() => {
    if (state.screenFlash) {
      const t = setTimeout(() => dispatch({ type: 'CLEAR_FLASH' }), 340);
      return () => clearTimeout(t);
    }
  }, [state.screenFlash]);

  const persist = (next) => { setSettings(next); localStorage.setItem('wq_arena3d_settings', JSON.stringify(next)); };

  // Shared authoritative combat positions — camera, scene, and occlusion
  // component all read from this single source of truth. Must be declared
  // before any early return so hook order stays stable.
  const chSnap = battle?.challenger_snapshot || {};
  const opSnap = battle?.opponent_snapshot || {};
  const combatPositions = useMemo(
    () => computeCombatPositions(chSnap, opSnap),
    [chSnap, opSnap],
  );

  // Reduced-motion fallback → serve the 2D LiveArena (safe, tested)
  if (settings.reducedMotion) {
    return <LiveArena />;
  }

  if (loading) return (
    <div className="p-10 text-center" data-testid="arena3d-loading">
      <Loader2 className="w-6 h-6 animate-spin mx-auto text-accent" />
    </div>
  );
  if (error) return (
    <div className="p-10 text-center text-muted-foreground" data-testid="arena3d-error">
      <p>{error}</p>
      <button onClick={() => nav('/battle')} className="mt-3 px-3 py-1.5 rounded-full bg-white/5 text-[11px] uppercase tracking-widest">Back</button>
    </div>
  );

  const bp = battle?.battle_prep || {};
  const winner = battle?.winner === 'challenger' ? 'challenger' : 'opponent';
  const currentLogLine = state.logLines[0];
  const arena = (battle?.arena || bp.arena || 'forest').toLowerCase();
  const weather = (battle?.weather || bp.weather || 'clear').toLowerCase();

  return (
    <div
      className="relative w-full h-[calc(100vh-3.5rem)] rounded-3xl overflow-hidden bg-black"
      data-testid="arena3d-root"
      data-arena={arena}
      data-weather={weather}
    >
      {/* --- 3D WebGL scene --- */}
      <div className="absolute inset-0" data-testid="arena3d-canvas">
        <Canvas
          dpr={[1, settings.lowPower ? 1.2 : 1.8]}
          gl={{ antialias: !settings.lowPower, powerPreference: 'high-performance' }}
          camera={{ position: [0, 3.2, 8.5], fov: 42, near: 0.1, far: 200 }}
          shadows={!settings.lowPower}
        >
          <AdaptiveDpr pixelated />
          <AdaptiveEvents />
          <Suspense fallback={null}>
            <CinematicScene
              arena={arena}
              weather={weather}
              chSnap={chSnap}
              opSnap={opSnap}
              chHp={state.hp.ch}
              opHp={state.hp.op}
              lastLineKind={state.lastLineKind}
              cursor={state.cursor}
              lowPower={settings.lowPower}
              consumedItem={consumedItem}
            />
            <CinematicCamera
              chSnap={chSnap}
              opSnap={opSnap}
              chPosition={combatPositions.chPos}
              opPosition={combatPositions.opPos}
              lastLineKind={state.lastLineKind}
              cursor={state.cursor}
              finished={state.finished}
              lowPower={settings.lowPower}
              slowMoUntil={state.slowMoUntil}
            />
            <Preload all />
          </Suspense>
          {settings.postFx && !settings.lowPower && (
            <EffectComposer disableNormalPass multisampling={0}>
              <Bloom intensity={0.55} luminanceThreshold={0.35} luminanceSmoothing={0.9} mipmapBlur />
              <ChromaticAberration offset={[0.0006, 0.0009]} />
              <Vignette eskil={false} offset={0.24} darkness={0.7} />
            </EffectComposer>
          )}
        </Canvas>
      </div>

      {/* --- HUD overlay --- */}
      <div className="relative z-10 h-full flex flex-col p-3 sm:p-5 pointer-events-none">
        {/* Top strip */}
        <div className="flex items-center gap-2 justify-between flex-wrap pointer-events-auto">
          <button onClick={() => nav('/battle')} data-testid="arena3d-exit"
            className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full glass border border-white/10 flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Exit
          </button>
          <div className="flex items-center gap-2">
            <TopChip label={arena} testid="pill-arena" />
            <TopChip label={weather} testid="pill-weather" />
            <TopChip label={`${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`} testid="arena3d-timer" mono />
          </div>
          <button onClick={() => setSettingsOpen(true)} data-testid="arena3d-settings-btn"
            className="p-1.5 rounded-full glass border border-white/10">
            <Settings2 className="w-4 h-4" />
          </button>
        </div>

        {/* Combatant name plates */}
        <div className="flex justify-between mt-3 pointer-events-none">
          <NamePlate side="ch" name={chSnap.name || 'Challenger'} rarity={chSnap.rarity} hp={state.hp.ch} color="#4ade80" testid="creature-challenger" />
          <NamePlate side="op" name={opSnap.name || 'Opponent'} rarity={opSnap.rarity} hp={state.hp.op} color="#f87171" testid="creature-opponent" align="right" />
        </div>

        <div className="flex-1" />

        {/* Log ticker */}
        <div className="flex flex-col items-center justify-center gap-2 pb-2 pointer-events-none">
          <AnimatePresence mode="popLayout">
            {currentLogLine && (
              <motion.p
                key={state.cursor}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28 }}
                data-testid="arena3d-log-line"
                className={`text-center max-w-md text-sm sm:text-base font-medium px-4 py-2 rounded-2xl backdrop-blur-md bg-black/45 border border-white/10 ${
                  currentLogLine.kind === 'crit' ? 'text-amber-300' :
                  currentLogLine.kind === 'dodge' ? 'text-cyan-300' :
                  currentLogLine.kind === 'combo' ? 'text-fuchsia-300 italic' :
                  currentLogLine.kind === 'round_intro' ? 'text-[10px] uppercase tracking-[0.3em] text-accent' :
                  currentLogLine.kind === 'flavor' ? 'text-emerald-200 italic' :
                  'text-white/95'
                }`}
              >{currentLogLine.text}</motion.p>
            )}
          </AnimatePresence>
          {state.comboCounter >= 5 && (
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              key={`combo-${state.comboTierIndex}`}
              data-testid="arena3d-combo"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-fuchsia-400/40">
              <Flame className="w-3.5 h-3.5 text-fuchsia-300" />
              <span className="font-mono text-lg text-fuchsia-100">{state.comboCounter}</span>
              <span className="text-[10px] uppercase tracking-widest text-fuchsia-300">combo</span>
            </motion.div>
          )}
          <AnimatePresence>
            {consumedItem && (
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.28 }}
                data-testid="consumable-tooltip"
                className="rounded-2xl px-3 py-2 flex items-center gap-2 backdrop-blur-md pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${consumedItem.color}22, rgba(0,0,0,0.55))`,
                         border: `1px solid ${consumedItem.color}55`,
                         boxShadow: `0 0 30px -6px ${consumedItem.color}` }}>
                <FlaskConical className="w-4 h-4" style={{ color: consumedItem.color }} />
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-semibold text-white/95">{consumedItem.name}</span>
                  <span className="text-[9px] uppercase tracking-widest" style={{ color: consumedItem.color }}>{consumedItem.description}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Primed consumables strip */}
        <div className="pointer-events-auto pb-2">
          <PrimedStrip consumables={loc.state?.consumables || []} />
        </div>

        {/* Tap + Ultimate */}
        <div className="mt-1 flex items-stretch gap-3 pointer-events-auto">
          <TapZone
            onTap={() => dispatch({ type: 'TAP' })}
            frozen={Date.now() < state.frozenUntil}
            energy={state.tapEnergy}
            costHint={state.queue[state.cursor]?.tap_cost || 1}
          />
          <UltimateButton
            charge={state.ultimateCharge}
            fired={state.ultimateFired}
            onFire={() => dispatch({ type: 'FIRE_ULTIMATE' })}
          />
        </div>
      </div>

      {/* Settings sheet */}
      <AnimatePresence>
        {settingsOpen && (
          <SettingsSheet
            settings={settings}
            onClose={() => setSettingsOpen(false)}
            onChange={persist}
            onReplayTutorial={() => {
              try { localStorage.removeItem('wq_battle_intro_seen'); } catch (_e) { /* ignore */ }
              setShowTutorial(true);
              setSettingsOpen(false);
              // Reset the force flag after mount so future battles don't auto-show.
              setTimeout(() => setShowTutorial(false), 100);
            }}
          />
        )}
      </AnimatePresence>

      {/* End-of-battle overlay */}
      <AnimatePresence>
        {state.finished && (
          <EndOverlay
            winner={winner}
            chName={chSnap.name || 'Challenger'}
            opName={opSnap.name || 'Opponent'}
            roundWins={battle?.round_wins || { challenger: 0, opponent: 0 }}
            coinsAwarded={battle?.coins_awarded}
            onExit={() => nav('/battle')}
          />
        )}
      </AnimatePresence>

      {/* First-time battle tutorial (also re-shown from Settings). */}
      <BattleInstructions force={showTutorial} />
    </div>
  );
}


// --- HUD helpers ---
function TopChip({ label, testid, mono }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full glass border border-white/10 text-[9px] uppercase tracking-widest text-white/85 capitalize" data-testid={testid}>
      <span className={mono ? 'font-mono tabular-nums' : ''}>{label}</span>
    </span>
  );
}

function NamePlate({ side, name, rarity, hp, color, testid, align }) {
  const pct = Math.max(0, Math.min(1, hp || 0)) * 100;
  return (
    <div className={`flex flex-col ${align === 'right' ? 'items-end' : 'items-start'}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        {rarity && (
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md"
            style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>{rarity}</span>
        )}
        <span className="font-serif text-lg leading-tight text-white/95 drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">{name}</span>
      </div>
      <div className="w-40 h-1.5 mt-1 rounded-full bg-black/70 border border-white/10 overflow-hidden"
           data-testid={`arena3d-${side === 'ch' ? 'challenger' : 'opponent'}-hp`}>
        <motion.div className="h-full"
          style={{ background: `linear-gradient(90deg, ${color}, ${color}88)`, boxShadow: `0 0 10px ${color}` }}
          animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: 'easeOut' }} />
      </div>
    </div>
  );
}

function PrimedStrip({ consumables }) {
  if (!consumables?.length) return null;
  return (
    <div className="flex items-center justify-center gap-1.5" data-testid="arena3d-consumables">
      {consumables.slice(0, 2).map((c, i) => {
        const meta = effectKindMeta(c);
        return (
          <div key={i} data-testid="arena3d-consumable-primed"
               title={meta.description}
               className="rounded-full px-3 py-1 text-[9px] uppercase tracking-widest border flex items-center gap-1"
               style={{ background: `linear-gradient(135deg, ${meta.color}22, rgba(0,0,0,0.35))`,
                        borderColor: `${meta.color}55`, color: '#e5e7eb' }}>
            <FlaskConical className="w-3 h-3" style={{ color: meta.color }} /> {(c.name || 'Item').split(' ')[0]}
          </div>
        );
      })}
    </div>
  );
}

function TapZone({ onTap, frozen, energy, costHint }) {
  const pct = Math.min(100, (energy / (costHint || 1)) * 100);
  return (
    <button
      onPointerDown={() => { if (!frozen) onTap(); }}
      disabled={frozen}
      data-testid="arena3d-tap-zone"
      data-frozen={frozen ? 'true' : 'false'}
      className="relative flex-1 h-24 rounded-3xl border border-emerald-400/40 overflow-hidden bg-gradient-to-br from-emerald-500/20 to-black/70 flex items-center justify-center touch-manipulation select-none disabled:opacity-40"
      style={{ WebkitTapHighlightColor: 'transparent', backdropFilter: 'blur(6px)' }}
    >
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/60">
        <div className="h-full bg-emerald-400 transition-all duration-150" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-col items-center gap-0.5 pointer-events-none">
        <Zap className={`w-6 h-6 text-emerald-300 ${frozen ? '' : 'animate-pulse'}`} strokeWidth={2} />
        <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-100">
          {frozen ? 'Consuming…' : 'Tap as fast as you can'}
        </span>
        {!frozen && (
          <span className="text-[9px] uppercase tracking-[0.25em] text-emerald-200/70">Faster = harder strikes</span>
        )}
      </div>
    </button>
  );
}

function UltimateButton({ charge, fired, onFire }) {
  const pct = Math.min(100, (charge / ULTIMATE_TAP_THRESHOLD) * 100);
  const ready = pct >= 100;
  return (
    <button onClick={onFire} disabled={!ready || fired}
      data-testid="arena3d-ultimate-btn"
      className={`relative w-24 h-24 rounded-3xl border overflow-hidden flex items-center justify-center transition-all
        ${ready && !fired ? 'border-amber-400/70 bg-amber-500/25 shadow-[0_0_36px_-4px_rgba(251,191,36,0.7)]' : 'border-white/10 bg-black/50'}
        ${!ready || fired ? 'opacity-70' : ''}`}
      style={{ backdropFilter: 'blur(6px)' }}>
      <div className="absolute inset-0" style={{
        background: `conic-gradient(from -90deg, rgba(251,191,36,0.55) ${pct * 3.6}deg, transparent 0deg)` }} />
      <div className="relative flex flex-col items-center">
        <Sparkles className={`w-6 h-6 ${ready ? 'text-amber-300' : 'text-white/50'} ${ready ? 'animate-pulse' : ''}`} strokeWidth={2} />
        <span className="text-[9px] uppercase tracking-widest mt-0.5">{fired ? 'Used' : ready ? 'Ultimate' : `${Math.round(pct)}%`}</span>
      </div>
    </button>
  );
}

function SettingsSheet({ settings, onClose, onChange, onReplayTutorial }) {
  const toggle = (k) => onChange({ ...settings, [k]: !settings[k] });
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-30 bg-black/70 flex items-end sm:items-center justify-center p-4"
      onClick={onClose} data-testid="arena3d-settings-sheet">
      <motion.div onClick={e => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="w-full max-w-sm rounded-3xl bg-[#0e1613] border border-white/10 p-5 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Display</p>
        <Toggle label="Reduced motion (2D fallback)" testid="opt-reduced-motion" active={settings.reducedMotion} onClick={() => toggle('reducedMotion')} />
        <Toggle label="Low-power mode (mobile/battery)" testid="opt-low-power" active={settings.lowPower} onClick={() => toggle('lowPower')} />
        <Toggle label="Post-processing (bloom · vignette)" testid="opt-post-fx" active={settings.postFx} onClick={() => toggle('postFx')} />
        {onReplayTutorial && (
          <button
            onClick={onReplayTutorial}
            data-testid="opt-replay-tutorial"
            className="w-full flex items-center justify-between p-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
          >
            <span className="text-sm">Replay battle tutorial</span>
            <span className="text-[10px] uppercase tracking-widest text-accent">Show →</span>
          </button>
        )}
        <p className="text-[10px] text-muted-foreground pt-1">
          Accessibility options are visual only. Outcome is decided by the hidden combat engine.
        </p>
      </motion.div>
    </motion.div>
  );
}

function Toggle({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center justify-between p-3 rounded-2xl border ${active ? 'border-accent/60 bg-accent/10' : 'border-white/10 bg-white/5'}`}>
      <span className="text-sm">{label}</span>
      <span className={`inline-block w-8 h-4 rounded-full ${active ? 'bg-accent' : 'bg-white/20'} relative transition-colors`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${active ? 'left-4' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

function EndOverlay({ winner, chName, opName, roundWins, coinsAwarded, onExit }) {
  const won = winner === 'challenger';
  const label = won ? 'Victory' : 'Defeat';
  const winnerName = won ? chName : opName;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-30 bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
      data-testid="arena3d-end">
      <motion.div initial={{ scale: 0.86, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-sm space-y-4">
        <div className={`inline-flex items-center gap-2 px-4 py-1 rounded-full ${won ? 'bg-amber-500/15 border border-amber-400/40 text-amber-200' : 'bg-red-500/10 border border-red-400/30 text-red-200'}`}>
          <Award className="w-3.5 h-3.5" />
          <span className="text-[10px] uppercase tracking-[0.3em]">{label}</span>
        </div>
        <h2 className="font-serif text-5xl">{winnerName}</h2>
        <p className="text-xs text-muted-foreground uppercase tracking-widest">takes the arena</p>
        <div className="flex items-center justify-center gap-4">
          <span className="font-mono text-2xl text-emerald-300">{roundWins.challenger}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">rounds</span>
          <span className="font-mono text-2xl text-red-300">{roundWins.opponent}</span>
        </div>
        {coinsAwarded && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-white/10">
            <span className="text-xs">Balance:</span>
            <span className="font-mono text-lg text-accent">{coinsAwarded.new_balance}</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">wildcoins</span>
          </div>
        )}
        <button onClick={onExit} data-testid="arena3d-end-exit"
          className="mt-2 px-6 py-2.5 rounded-full bg-accent text-black font-semibold text-sm uppercase tracking-widest">
          Continue
        </button>
      </motion.div>
    </motion.div>
  );
}
